// Phone validation and formatting utilities
// This module provides consistent phone number handling across the application

// Constants
export const PHONE_LENGTH = 12; // +1 + 10 digits
export const PHONE_DIGITS_ONLY_LENGTH = 10; // US phone number without country code
export const PHONE_COUNTRY_CODE = '+1';
export const PHONE_REGEX = /^\+1\d{10}$/; // +1 followed by exactly 10 digits

// Validation functions
export const isValidPhoneNumber = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  return PHONE_REGEX.test(phone) && phone.length === PHONE_LENGTH;
};

export const isValidPhoneLength = (phone: string): boolean => {
  if (!phone) return false;

  // If it's a full phone number with country code, check full length
  if (phone.startsWith(PHONE_COUNTRY_CODE)) {
    return phone.length === PHONE_LENGTH;
  }

  // For partial numbers during typing, check if we have exactly 10 digits
  const digits = phone.replace(/\D/g, '');
  return digits.length === PHONE_DIGITS_ONLY_LENGTH;
};

// Formatting functions
export const formatPhoneForInput = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');

  // Remove leading 1 if present (country code)
  const cleanDigits = digits.startsWith('1') ? digits.slice(1) : digits;

  // Format as (XXX)-XXX-XXXX
  if (cleanDigits.length >= 10) {
    const area = cleanDigits.slice(0, 3);
    const exchange = cleanDigits.slice(3, 6);
    const number = cleanDigits.slice(6, 10);
    return `+1 (${area})-${exchange}-${number}`;
  }

  return phone;
};

export const createFullPhoneNumber = (digitsOnly: string): string => {
  // Remove any non-digits and ensure it's exactly 10 digits
  const cleanDigits = digitsOnly.replace(/\D/g, '');

  if (cleanDigits.length !== PHONE_DIGITS_ONLY_LENGTH) {
    throw new Error(`Phone number must be exactly ${PHONE_DIGITS_ONLY_LENGTH} digits`);
  }

  return `${PHONE_COUNTRY_CODE}${cleanDigits}`;
};

// Helper for react-number-format onChange
export const handlePhoneInputChange = (
  e: { value: string; formattedValue: string },
  onPhoneChange: (phone: string, formatted: string) => void,
): void => {
  // Remove any non-digits
  const cleanDigits = e.value.replace(/\D/g, '');

  // Only create full phone number if we have exactly 10 digits
  if (cleanDigits.length === PHONE_DIGITS_ONLY_LENGTH) {
    const fullPhone = createFullPhoneNumber(e.value);
    onPhoneChange(fullPhone, e.formattedValue);
  } else {
    // For incomplete numbers, just pass the raw value
    onPhoneChange(e.value, e.formattedValue);
  }
};

// Validation for forms
export const validatePhoneForSubmission = (phone: string): { isValid: boolean; error?: string } => {
  if (!phone) {
    return { isValid: false, error: 'Phone number is required' };
  }

  if (!isValidPhoneNumber(phone)) {
    return { isValid: false, error: 'Please enter a valid US phone number' };
  }

  return { isValid: true };
};

// Pattern for react-number-format
export const PHONE_INPUT_FORMAT = '+1 (###)-###-####';
export const PHONE_INPUT_PLACEHOLDER = '+1 (305)-123-4567';

/** Canonicalize supported US numbers without conflating other country codes or partial input. */
export const canonicalizeMobileNumber = (phone: string): string => {
  if (!/^[\s().-]*(?:\+?1[\s().-]*)?(?:\d[\s().-]*){10}$/.test(phone)) return phone;
  const digits = phone.replace(/\D/g, '');
  return `+1${digits.length === 11 ? digits.slice(1) : digits}`;
};
